import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  getBulletinById,
  getOrgConfig,
  getPoliceServices,
  getUserMeGroups,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import {
  IDLE_GUARD,
  useNavigationGuard,
  type NavigationGuard,
} from "@/hooks/useNavigationGuard";
import {
  BulletinPatchPayload,
  BulletinResourceResponse,
  IAlertGroup,
  IAlertProperties,
} from "@/types";

import {
  defaultAlertProperties,
  defaultErrors,
  IFieldErrors,
} from "./constants";
import { updateToContextStructure } from "./utils";

// Define IOrgConfig interface
export interface IOrgConfig {
  default_filters?: Record<string, any>;
  [key: string]: any;
}

export const SingleAlertContext = createContext<{
  alertProperties: IAlertProperties;
  setAlertProperties: React.Dispatch<React.SetStateAction<IAlertProperties>>;
  setAlertProperty: (key: string, value: any) => void;
  errors: IFieldErrors;
  setErrors: React.Dispatch<React.SetStateAction<IFieldErrors>>;
  refreshAlertData: (id: string) => void;
}>({
  alertProperties: defaultAlertProperties,
  setAlertProperties: () => {},
  setAlertProperty: () => {},
  errors: defaultErrors,
  setErrors: () => {},
  refreshAlertData: () => {},
});

interface SingleAlertProviderProps {
  children: ReactNode;
}

export type { IFieldErrors };

export const SingleAlertProvider: React.FC<SingleAlertProviderProps> = ({
  children,
}) => {
  const [alertProperties, setAlertProperties] = useState<IAlertProperties>(
    defaultAlertProperties,
  );
  const [errors, setErrors] = useState<IFieldErrors>(defaultErrors);

  const setAlertProperty = (key: string, value: any) => {
    setAlertProperties((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const refreshAlertData = (id: string) => {
    if (id) {
      getBulletinById({ path: { bulletin_id: parseInt(id) } })
        .then((res) => {
          const alertProperties = updateToContextStructure(res.data!);
          setAlertProperties(alertProperties);
        })
        .catch((err) => {
          toast.error(err);
        });
    }
  };

  return (
    <SingleAlertContext.Provider
      value={{
        alertProperties,
        setAlertProperties,
        setAlertProperty,
        errors,
        setErrors,
        refreshAlertData,
      }}
    >
      {children}
    </SingleAlertContext.Provider>
  );
};

// Context for AlertView - All alerts page

interface IAlertViewContext {
  totalAlerts: number;
  setTotalAlerts: React.Dispatch<React.SetStateAction<number>>;
  currentItems: BulletinResourceResponse[];
  setCurrentItems: React.Dispatch<
    React.SetStateAction<BulletinResourceResponse[]>
  >;
  updatedItems: BulletinPatchPayload[];
  setUpdatedItems: React.Dispatch<React.SetStateAction<BulletinPatchPayload[]>>;
  policeServices: IAlertGroup[];
  setPoliceServices: React.Dispatch<React.SetStateAction<IAlertGroup[]>>;
  filtersVisible: boolean;
  setFiltersVisible: React.Dispatch<React.SetStateAction<boolean>>;
  bulletinId: number;
  setBulletinId: React.Dispatch<React.SetStateAction<number>>;
  index: number;
  setIndex: React.Dispatch<React.SetStateAction<number>>;
  orgConfig: IOrgConfig | null;
  setOrgConfig: React.Dispatch<React.SetStateAction<IOrgConfig | null>>;
  onlyMeHasAccess: boolean;
  setOnlyMeHasAccess: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Blocks navigation away from a bulletin only the current user can see, to
   * remind them to share it. DisplayAlertWrapper renders the prompt.
   */
  accessGuard: NavigationGuard;
  scrollToBulletinId: number | null;
  setScrollToBulletinId: React.Dispatch<React.SetStateAction<number | null>>;
}

export const AlertViewContext = createContext<IAlertViewContext>({
  totalAlerts: 0,
  setTotalAlerts: () => {},
  currentItems: [],
  setCurrentItems: () => {},
  updatedItems: [],
  setUpdatedItems: () => {},
  policeServices: [],
  setPoliceServices: () => {},
  filtersVisible: false,
  setFiltersVisible: () => {},
  bulletinId: 0,
  setBulletinId: () => {},
  index: 0,
  setIndex: () => {},
  orgConfig: null,
  setOrgConfig: () => {},
  onlyMeHasAccess: false,
  setOnlyMeHasAccess: () => {},
  accessGuard: IDLE_GUARD,
  scrollToBulletinId: null,
  setScrollToBulletinId: () => {},
});

// Provider for Search and Filter and AlertView Page

interface AlertViewProviderProps {
  children: ReactNode;
}

export const AlertViewProvider: React.FC<AlertViewProviderProps> = ({
  children,
}) => {
  const [totalAlerts, setTotalAlerts] = useState<number>(0);
  const [currentItems, setCurrentItems] = useState<BulletinResourceResponse[]>(
    [],
  );
  const [updatedItems, setUpdatedItems] = useState<BulletinPatchPayload[]>([]);
  const [policeServices, setPoliceServices] = useState<IAlertGroup[]>([]);
  const [orgConfig, setOrgConfig] = useState<IOrgConfig | null>(null);
  const [onlyMeHasAccess, setOnlyMeHasAccess] = useState(false);
  const [scrollToBulletinId, setScrollToBulletinId] = useState<number | null>(
    null,
  );

  // Registered here because this provider owns onlyMeHasAccess. Every way out
  // of the open bulletin — clicking another one in the list, closing the
  // drawer, the sidebar, browser back — is a router navigation, so it is
  // covered without any of those call sites knowing about the guard.
  const accessGuard = useNavigationGuard(
    ({ currentLocation, nextLocation }) => {
      // Only ever true while the Access tab is open on a bulletin nobody else
      // can see; Access resets it on unmount.
      if (!onlyMeHasAccess) return false;

      const current = new URLSearchParams(currentLocation.search);
      const next = new URLSearchParams(nextLocation.search);

      // In compare mode, swapping or promoting the compared bulletin is not
      // leaving the bulletin whose access is being managed.
      if (current.get("pinToFront")) return false;

      return (
        currentLocation.pathname !== nextLocation.pathname ||
        current.get("bulletinId") !== next.get("bulletinId")
      );
    },
  );

  const { currentUser, setCurrentUserGroups, setCurrentUserAgency } =
    useContext(AuthStateContext);

  useEffect(() => {
    if (currentUser?.rubi_org_id) {
      getOrgConfig({ path: { rubi_org_id: currentUser.rubi_org_id } }).then(
        (res) => {
          setOrgConfig(res.data as IOrgConfig);
        },
      );
    }
  }, [currentUser?.rubi_org_id]);

  useEffect(() => {
    if (currentUser) {
      const currentUserPermissions =
        currentUser.permissions?.map((permission) => permission.title) || [];

      const userPermissions = userPermissionChecks(currentUserPermissions);

      if (!userPermissions.canAccessAlertsApp) {
        return;
      }
      getPoliceServices().then((res) => {
        setPoliceServices(res.data as IAlertGroup[]);
      });
      getUserMeGroups().then((res) => {
        if (res.data) {
          const userGroups = res.data.items as IAlertGroup[];
          setCurrentUserGroups(userGroups);
          const foundAgency = userGroups?.find(
            (agency) => agency.is_police_service,
          );
          if (foundAgency) {
            setCurrentUserAgency(foundAgency);
          }
        }
      });
    }
  }, [currentUser]);

  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
  const [index, setIndex] = useState<number>(0);
  const [bulletinId, setBulletinId] = useState<number>(currentItems[index]?.id);

  return (
    <AlertViewContext.Provider
      value={{
        totalAlerts,
        setTotalAlerts,
        currentItems,
        setCurrentItems,
        updatedItems,
        setUpdatedItems,
        policeServices,
        setPoliceServices,
        filtersVisible,
        setFiltersVisible,
        bulletinId,
        setBulletinId,
        index,
        setIndex,
        orgConfig,
        setOrgConfig,
        onlyMeHasAccess,
        setOnlyMeHasAccess,
        accessGuard,
        scrollToBulletinId,
        setScrollToBulletinId,
      }}
    >
      {children}
    </AlertViewContext.Provider>
  );
};
